import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import { isAssessmentAgreement } from 'helpers/clientAgreements';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { ClientAgreementRow } from 'types';
import { downloadBlob } from 'utils/file';
import { getErrorMessage } from 'utils/v2/errorHelper';

export const isAssessmentRow = isAssessmentAgreement;

export const clientAgreementRowKey = (row: ClientAgreementRow, index: number): string =>
    String(row.agreement_id ?? row.id ?? `${row.application_id ?? 'row'}-${index}`);

/** Legacy PDF retainers are uploaded with `POST /agreement/:applicationId`. */
export const canUploadLegacyRetainer = (row: ClientAgreementRow): boolean =>
    !isAssessmentRow(row) && !row.agreement_type && Boolean(row.application_id);

const fileNameForRow = (row: ClientAgreementRow, key: string) => {
    const safeName =
        (row.program_name || 'agreement').replace(/[^a-zA-Z0-9]+/g, '_') || 'agreement';
    return `${safeName}_${key}.pdf`;
};

/**
 * `GET /users/:id/agreements` — one row per retainer. Downloads route by row
 * kind: assessment → assessment agreement, typed rows → template retainer
 * endpoints, `agreement_type: null` → legacy PDF agreement.
 */
export const useClientAgreementRows = (candidateId: string) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [rows, setRows] = useState<ClientAgreementRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const showError = useCallback(
        (error: unknown) =>
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            ),
        [dispatch, translate]
    );

    const reload = useCallback(async () => {
        if (!candidateId) return;
        setLoading(true);
        try {
            const response = await api.agreement.getAgreementTableData(candidateId);
            setRows((response?.data ?? []) as ClientAgreementRow[]);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [candidateId, showError]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const fetchFile = useCallback(
        async (row: ClientAgreementRow): Promise<Blob | null> => {
            if (isAssessmentRow(row)) {
                const { file } = await api.signRetainer.getAssessmentAgreementDocument(candidateId);
                return file;
            }
            if (row.agreement_type && row.application_id && row.agreement_id) {
                const { file } = await api.applicationRetainers.getDocument(
                    row.application_id,
                    row.agreement_id
                );
                return file;
            }
            if (row.application_id) {
                const { file } = await api.signRetainer.getAgreementDocument(
                    String(row.application_id)
                );
                return file;
            }
            return null;
        },
        [candidateId]
    );

    const download = useCallback(
        async (row: ClientAgreementRow, key: string) => {
            setBusyKey(key);
            try {
                const file = await fetchFile(row);
                if (!file) return;
                downloadBlob(file, fileNameForRow(row, key));
            } catch (error) {
                showError(error);
            } finally {
                setBusyKey(null);
            }
        },
        [fetchFile, showError]
    );

    const preview = useCallback(
        async (row: ClientAgreementRow, key: string): Promise<Blob | null> => {
            setBusyKey(key);
            try {
                return await fetchFile(row);
            } catch (error) {
                showError(error);
                return null;
            } finally {
                setBusyKey(null);
            }
        },
        [fetchFile, showError]
    );

    const sendReminder = useCallback(
        async (row: ClientAgreementRow, key: string) => {
            if (!row.application_id) return;
            setBusyKey(key);
            try {
                await api.sendAgreement.sendAgreement(row.application_id);
                dispatch(
                    setAlert({ message: translate('emailSuccessfullySent'), type: 'success' })
                );
            } catch (error) {
                showError(error);
            } finally {
                setBusyKey(null);
            }
        },
        [dispatch, showError, translate]
    );

    const removeAgreement = useCallback(
        async (agreementId: number | string) => {
            await api.agreement.deleteAgreement(agreementId);
            await reload();
        },
        [reload]
    );

    const upload = useCallback(
        async (row: ClientAgreementRow, file: File, key: string) => {
            if (!canUploadLegacyRetainer(row) || !row.application_id) return;
            setBusyKey(key);
            try {
                const formData = new FormData();
                formData.append('retainer_agreement', file, file.name);
                await api.agreement.post(row.application_id, formData);
                dispatch(setAlert({ message: translate('retainerUploaded'), type: 'success' }));
                await reload();
            } catch (error) {
                showError(error);
            } finally {
                setBusyKey(null);
            }
        },
        [dispatch, reload, showError, translate]
    );

    return {
        rows,
        loading,
        reload,
        download,
        preview,
        upload,
        sendReminder,
        removeAgreement,
        busyKey,
    };
};
